/**
 * Employee Profile (read view) — /admin/hr/employees/:id
 * EMP-WS-UI-001 (Sep 2026): premium shell shared with the Edit workspace (hero, overview
 * cards, activity timeline). Data sources, photo/link endpoints and RBAC unchanged.
 * HR-PHOTO-001 photo upload/remove · EMP-USER-LINK-001 link/unlink · leave balances read-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../ui/button';
import { ChevronLeft, Edit2, Loader2, FileText, Info, History, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { EmployeeMasterViewSections } from './EmployeeMasterFormSections';
import { isHrReadOnly } from '../../../lib/hrRole';
import LinkAccountModal from './workspace/LinkAccountModal';
import { EmployeeHero, usePhotoActions } from './workspace/EmployeeHero';
import { SectionCard, InfoTiles, AttendanceCard, LinkedAccountCard, LeaveBalancesCard } from './workspace/OverviewCards';
import { ActivityTimeline } from './workspace/ActivityTimeline';
import { EmployeeStatusCard } from './workspace/QuickActionsRail';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function EmployeeProfile() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [audit, setAudit] = useState([]);
  const [leaveBal, setLeaveBal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkModal, setLinkModal] = useState(null); // 'link' | 'unlink' | null
  const readOnly = isHrReadOnly();

  const load = useCallback(async () => {
    try {
      const headers = authHeaders();
      const [emp, log] = await Promise.all([
        axios.get(`${API}/api/hr/employees/${employeeId}?include_attendance=true`, { headers }),
        axios.get(`${API}/api/hr/audit-log?employee_id=${employeeId}&limit=15`, { headers }).catch(() => ({ data: { data: [] } })),
      ]);
      setEmployee(emp.data);
      setAudit(log.data.data || log.data.logs || []);
      // EMP-USER-LINK-001 — resolve via explicit link first, email fallback
      axios.get(`${API}/api/leave-registry/user-balance`, { headers, params: { employee_id: employeeId } })
        .then(res => setLeaveBal(res.data))
        .catch(() => setLeaveBal(null));
    } catch {
      toast.error('Failed to load employee profile');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);
  const photo = usePhotoActions(employeeId, (p) => setEmployee(prev => ({ ...prev, ...p })));

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!employee) {
    return <div className="p-12 text-center text-slate-500">Employee not found.</div>;
  }

  const details = [['Manager', employee.manager_name], ['Time Zone', employee.employee_timezone], ['Employment Type', employee.employment_type], ['Location', employee.location], ['Grade / Level', employee.grade_level], ['Notes', employee.notes]];

  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto space-y-4" data-testid="employee-profile">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[13px] text-slate-500">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/hr/employees')} className="an-btn h-8 rounded-full px-2.5" data-testid="profile-back-btn">
            <ChevronLeft className="w-4 h-4" /> Directory
          </Button>
          <span className="text-slate-300">/</span><span className="font-semibold text-slate-800">Employee Profile</span>
        </div>
        <p className="hidden md:block an-accent text-[18px] text-violet-500/90">People Make Progress</p>
      </div>

      <EmployeeHero employee={employee} readOnly={readOnly} onPhotoUpload={photo.upload} onPhotoRemove={photo.remove}>
        {!readOnly && (
          <Button onClick={() => navigate(`/admin/hr/employees/${employeeId}/edit`)} className="an-btn an-btn-primary h-10 rounded-xl gap-2 px-4" data-testid="profile-edit-btn">
            <Edit2 className="w-4 h-4" /> Edit Employee
          </Button>
        )}
      </EmployeeHero>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <SectionCard icon={Info} title="Employee Overview" subtitle="Key information at a glance" testid="profile-overview" delay={120}>
            <InfoTiles employee={employee} />
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3" data-testid="profile-details">
              {details.map(([label, value]) => (
                <div key={label} className="min-w-0"><p className="text-[11.5px] font-medium text-slate-400 uppercase tracking-wider">{label}</p><p className="text-[13.5px] font-medium text-slate-700 capitalize truncate">{value || '—'}</p></div>
              ))}
            </div>
          </SectionCard>
          <AttendanceCard att={employee.attendance_summary} delay={170} />
          <LinkedAccountCard employee={employee} readOnly={readOnly} onLink={() => setLinkModal('link')} onUnlink={() => setLinkModal('unlink')} delay={210} />
          <LeaveBalancesCard leaveBal={leaveBal} onHistory={() => navigate(`/admin/workspace?tab=leave&subtab=adjustments&user_id=${leaveBal.user_id}`)} delay={250} />
          <SectionCard icon={FileText} title="Master Records" subtitle="Personal, contact, statutory and banking details as authorised" testid="profile-master-records" delay={290}>
            <EmployeeMasterViewSections employee={employee} />
          </SectionCard>
          <SectionCard icon={History} title="Activity History" subtitle="Recent audited changes" testid="profile-activity" delay={330}
            action={<Link to={`/admin/hr/audit-log?employee_id=${encodeURIComponent(employeeId)}`} className="an-btn inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-violet-200 bg-white text-[12.5px] font-semibold text-violet-700 hover:bg-violet-50" data-testid="profile-view-full-audit">View Full Audit History <ArrowRight className="h-3.5 w-3.5" /></Link>}>
            <ActivityTimeline rows={audit} testid="profile-activity-list" />
          </SectionCard>
        </div>
        <aside className="space-y-4 min-w-0">
          <EmployeeStatusCard employee={employee} delay={160} />
        </aside>
      </div>

      {linkModal && (
        <LinkAccountModal mode={linkModal} employee={employee} onClose={() => setLinkModal(null)} onDone={() => { setLinkModal(null); load(); }} />
      )}
    </div>
  );
}
